import "@shoelace-style/shoelace/dist/components/badge/badge.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { fetchTaskAttachments } from "../../services/supabase";

/**
 * TaskCardFooter - Footer section of a task card
 *
 * @param {Object} props
 * @param {Object} props.task - Task data
 * @param {boolean} props.isEditing - Whether task is in edit mode
 * @param {boolean} props.hasAttachments - Whether task has attachments
 * @param {Array} props.fileUploads - Files being uploaded
 * @param {Function} props.onFileChange - File change handler
 * @param {React.RefObject} props.fileInputRef - Ref for file input
 */
const TaskCardFooter = ({
  task,
  isEditing,
  hasAttachments,
  fileUploads,
  onFileChange,
  fileInputRef,
}) => {
  const [attachmentCount, setAttachmentCount] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(
        window.innerWidth <= 768 ||
          "ontouchstart" in window ||
          navigator.maxTouchPoints > 0
      );
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Format created date
  const formattedDate = task.created_at
    ? format(new Date(task.created_at), "MMM d")
    : "";

  // Fetch attachment count
  useEffect(() => {
    if (hasAttachments && task.id) {
      const getAttachmentCount = async () => {
        try {
          const attachments = await fetchTaskAttachments(task.id);
          setAttachmentCount(attachments?.length || 0);
        } catch (error) {
          console.error("Error fetching attachment count:", error);
          setAttachmentCount(0);
        }
      };

      getAttachmentCount();
    }
  }, [hasAttachments, task.id]);

  // Icon size based on device
  const iconSize = isMobile ? "1.2rem" : "1rem";

  return (
    <div className="flex justify-between items-center text-xs mt-2">
      <div className="text-gray-500">{formattedDate}</div>

      <div className="flex items-center gap-2">
        {isEditing ? (
          <div className="flex items-center">
            <input
              ref={fileInputRef}
              type="file"
              onChange={onFileChange}
              className="hidden"
              multiple
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-gray-400 hover:text-[#4db1e0] p-1"
              title="Attach files"
            >
              <sl-icon
                name="paperclip"
                style={{ fontSize: iconSize }}
              ></sl-icon>
            </button>
            {fileUploads.length > 0 && (
              <span className="ml-1 text-xs text-gray-400">
                {fileUploads.length} file{fileUploads.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        ) : (
          hasAttachments && (
            <div className="flex items-center">
              <sl-icon
                name="paperclip"
                class="text-gray-400"
                style={{ fontSize: iconSize }}
              ></sl-icon>
              <sl-badge variant="primary" pill className="ml-1">
                <span className="text-[10px]">{attachmentCount}</span>
              </sl-badge>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default TaskCardFooter;
